import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import {
  MdOutlineTaskAlt,
  MdAddTask,
  MdOutlineBook,
  MdOutlineArticle,
  MdOutlineSettings,
} from "react-icons/md";
import { LuHistory } from "react-icons/lu";
import { SidebarSkeleton } from "./ui/Skeleton";

const routeToTabMap = {
  "/myTask": "My Tasks",
  "/history": "My Tasks History",
  "/assignment": "Task Assignment",
  "/issues": "Issue Overview",
  "/journals": "Journal Overview",
  "/settings": "Settings",
};

const Sidebar = ({ isOpen, activeTab, setActiveTab, isLoading }) => {
  const location = useLocation();
  const { userData } = useSelector((state) => state.user);

  useEffect(() => {
    const tabName = routeToTabMap[location.pathname];
    if (tabName) {
      setActiveTab(tabName);
    }
  }, [location.pathname, setActiveTab]);

  const hasTaskManagementPermission = () => {
    return (
      userData.articleTaskManagement === "View" ||
      userData.articleTaskManagement === "Edit" ||
      userData.issueTaskManagement === "View" ||
      userData.issueTaskManagement === "Edit"
    );
  };

  const hasOverviewPermission = (type) => {
    return (
      userData[`${type}Overview`] === "View" ||
      userData[`${type}Overview`] === "Edit"
    );
  };

  const baseMenuItems = [
    { icon: <MdOutlineTaskAlt size={20} />, text: "My Tasks", path: "/myTask" },
    {
      icon: <LuHistory size={20} />,
      text: "My Tasks History",
      path: "/history",
    },
  ];

  const menuItems = [
    ...baseMenuItems,
    ...(hasTaskManagementPermission()
      ? [
          {
            icon: <MdAddTask size={20} />,
            text: "Task Assignment",
            path: "/assignment",
          },
        ]
      : []),
  ];

  const overviewItems = [
    ...(hasOverviewPermission("issue")
      ? [
          {
            icon: <MdOutlineBook size={20} />,
            text: "Issue Overview",
            path: "/issues",
          },
        ]
      : []),
    ...(hasOverviewPermission("article")
      ? [
          {
            icon: <MdOutlineArticle size={20} />,
            text: "Journal Overview",
            path: "/journals",
          },
        ]
      : []),
  ];

  const renderNavItem = ({ icon, text, path }) => (
    <li key={path}>
      <NavLink
        to={path}
        onClick={() => setActiveTab(text)}
        className={`flex items-center rounded hover:bg-[#EDF0FC] h-[48px]
          ${
            activeTab === text ? "bg-[#E1E6FF] border-l-4 border-[#1C40C9]" : ""
          }
          ${isOpen ? "px-2" : "w-[48px] justify-center"}
        `}
        aria-label={isOpen ? text : `${text} (collapsed)`}
      >
        <span
          className={`relative group flex items-center justify-center px-3
            ${activeTab === text ? "text-[#35424D]" : "text-[#5D6871]"}
          `}
        >
          {icon}
          {!isOpen && (
            <div
              className="absolute left-[65px] z-[70] top-1/2 -translate-y-1/2 px-3 py-1 rounded bg-gray-800 text-white text-sm opacity-0 group-hover:opacity-100 whitespace-nowrap shadow-md transition-opacity duration-200 pointer-events-none
            before:border-r-gray-800 before:-translate-y-1/2 before:border-4 before:border-transparent before:absolute before:right-full before:top-1/2"
            >
              {text}
            </div>
          )}
        </span>

        {/* Label text */}
        <span
          className={`font-semibold whitespace-nowrap overflow-hidden
            ${isOpen ? "w-auto opacity-100" : "w-0 opacity-0"}
            ${activeTab === text ? "text-[#35424D]" : "text-[#5D6871]"}
          `}
        >
          {text}
        </span>
      </NavLink>
    </li>
  );

  return (
    <div
      className="bg-white shadow-md border-r font-semibold border-[#B7B7B7] duration-300"
      style={{
        width: isOpen ? "220px" : "64px",
        height: "calc(100vh - 72px)",
        paddingTop: "8px",
        fontSize: "16px",
        fontFamily: "'Source Sans Pro'",
      }}
      aria-expanded={isOpen}
    >
      <div className="flex flex-col h-full">
        <nav className="flex-1 px-2" aria-label="Main navigation">
          <ul className="space-y-1">
            {isLoading ? (
              <SidebarSkeleton
                isOpen={isOpen}
                menuCount={menuItems.length}
                overviewCount={overviewItems.length}
                settingsCount={
                  userData.settings &&
                  (userData.settings === "View" || userData.settings === "Edit")
                    ? 1
                    : 0
                }
              />
            ) : (
              <>
                {menuItems.map(renderNavItem)}

                {/* {menuItems.length > 0 && overviewItems.length > 0 && (
                  <hr className="border-gray-300 my-2" />
                )} */}

                {overviewItems.map(renderNavItem)}

                {/* {(overviewItems.length > 0 ||
                  (userData.settings &&
                    (userData.settings === "View" ||
                      userData.settings === "Edit"))) && (
                  <hr className="border-gray-300 my-2" />
                )} */}

                {userData.settings &&
                  (userData.settings === "View" ||
                    userData.settings === "Edit") &&
                  renderNavItem({
                    icon: <MdOutlineSettings size={20} />,
                    text: "Settings",
                    path: "/settings",
                  })}
              </>
            )}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
